import type { PrijsfavorietenPart, PrijsfavorietenPayload } from './report';

/*
 * Two fallbacks for when the live report isn't available:
 *
 *   PF_PREVIEW — the design handoff's exact numbers. DEV ONLY, so step 1 (UI
 *     before the pipeline exists) can be reviewed as the real thing.
 *   PF_EMPTY   — the same structure with zeros. What a DEPLOYED build shows,
 *     so a client can never be served invented figures. Same discipline as the
 *     AH report's AH_EMPTY.
 *
 * See usePrijsfavorietenReport for the selection.
 */

// 24 aug → 13 sep 2026 — the full 21-day window (dev-team spec). The design
// charted only the 14 elapsed days; the remaining days are zero-filled, which
// is exactly what the pipeline emits mid-campaign.
const DAYS = [
  '2026-08-24',
  '2026-08-25',
  '2026-08-26',
  '2026-08-27',
  '2026-08-28',
  '2026-08-29',
  '2026-08-30',
  '2026-08-31',
  '2026-09-01',
  '2026-09-02',
  '2026-09-03',
  '2026-09-04',
  '2026-09-05',
  '2026-09-06',
  '2026-09-07',
  '2026-09-08',
  '2026-09-09',
  '2026-09-10',
  '2026-09-11',
  '2026-09-12',
  '2026-09-13',
];

// Design series are in thousands per day; stored here as real counts so the
// pipeline can emit plain numbers and the chart needs no unit convention.
const GAMES_K = [245, 262, 231, 208, 196, 182, 174, 248, 218, 190, 176, 168, 158, 147, 0, 0, 0, 0, 0, 0, 0];
const VISITORS_K = [68, 58, 46, 40, 36, 32, 30, 48, 40, 34, 30, 28, 25, 22, 0, 0, 0, 0, 0, 0, 0];

export const PF_PREVIEW: PrijsfavorietenPayload = {
  campaign: {
    name: 'AH Prijsfavorieten',
    start: '24 aug 2026',
    end: '13 sep 2026',
    status: 'In voortgang',
    currentDay: 15,
    totalDays: 21,
    currentWeek: 2,
    pace: '~200.137 games / dag',
    weeksLabel: '3 × 1 week',
    periodLabel: 'Periode 1',
    calendarWeeks: 'CW 35 / 36 / 37',
    live: true,
  },
  weeks: [
    { label: 'wk 1', dates: '24 – 30 aug' },
    { label: 'wk 2', dates: '31 aug – 6 sep' },
    { label: 'wk 3', dates: '7 – 13 sep', planned: true },
  ],
  weekly: {
    label: 'Overzicht per week',
    columns: ['WK 1', 'WK 2', 'WK 3'],
    rows: [
      { label: 'Unieke bezoekers', values: [298_140, 237_552, null] },
      { label: 'Unieke spelers', values: [212_455, 161_633, null] },
      { label: 'Aantal bezoeken', values: [682_914, 571_383, null] },
      { label: 'Tijd in campagne', values: ['84.905u', '67.415u', null] },
      { label: 'Games gespeeld', values: [1_506_318, 1_295_601, null] },
    ],
  },
  days: DAYS,
  series: {
    games: GAMES_K.map((k) => k * 1000),
    visitors: VISITORS_K.map((k) => k * 1000),
  },
  totals: {
    uniqueVisitors: 535_692,
    avgVisitorsPerDay: 38_264,
    uniquePlayers: 374_088,
    visits: 1_254_297,
    timeInCampaign: '152.320u',
    avgTimePerUser: '17m 4s',
    avgSessionDuration: '7m 17s',
    gamesPlayed: 2_801_919,
    avgGamesPerPlayer: 7.49,
  },
  gameplay: {
    distribution: [
      { label: '1 game', pct: 22.9 },
      { label: '2 games', pct: 11.8 },
      { label: '3 games', pct: 8.9 },
      { label: '4 games', pct: 7.1 },
      { label: '5+ games', pct: 49.4 },
    ],
    repeat: {
      allWeeksPct: 39.5,
      rows: [
        { label: 'Alle weken', pct: 39.5 },
        { label: 'Twee weken', pct: 30.2 },
        { label: 'Eén week', pct: 30.3 },
      ],
    },
  },
  behaviour: {
    infoPagesTotal: 68_261,
    topPages: [
      { name: 'Voorwaarden', value: 45_699 },
      { name: 'Hoe werkt het', value: 5_175 },
      { name: 'Veelgestelde vragen', value: 1_948 },
    ],
  },
};

/** Same shape, all zeros — the deployed no-live-data state. */
export const PF_EMPTY: PrijsfavorietenPayload = {
  campaign: { ...PF_PREVIEW.campaign, currentDay: 0, currentWeek: 1, pace: '—', status: '—', live: false },
  weeks: PF_PREVIEW.weeks.map((w) => ({ ...w })),
  weekly: { ...PF_PREVIEW.weekly, rows: PF_PREVIEW.weekly.rows.map((r) => ({ ...r, values: r.values.map(() => null) })) },
  days: DAYS,
  series: { games: DAYS.map(() => 0), visitors: DAYS.map(() => 0) },
  totals: {
    uniqueVisitors: 0,
    avgVisitorsPerDay: 0,
    uniquePlayers: 0,
    visits: 0,
    timeInCampaign: '0u',
    avgTimePerUser: '0m',
    avgSessionDuration: '0m',
    gamesPlayed: 0,
    avgGamesPerPlayer: 0,
  },
  gameplay: {
    distribution: PF_PREVIEW.gameplay.distribution.map((d) => ({ ...d, pct: 0 })),
    repeat: { allWeeksPct: 0, rows: PF_PREVIEW.gameplay.repeat.rows.map((r) => ({ ...r, pct: 0 })) },
  },
  behaviour: { infoPagesTotal: 0, topPages: PF_PREVIEW.behaviour.topPages.map((p) => ({ ...p, value: 0 })) },
};

/*
 * Deel 2 — the design "AH Prijsfavorieten Dashboard – Deel 2 wk 1": day 4 of
 * Deel 2 (28 sep → 18 okt), compared with a finished Deel 1. DEV ONLY, like
 * PF_PREVIEW: in a deployed build Deel 1 comes from the pipeline's frozen
 * snapshot, never from here.
 */
const dayRange = (start: string, n: number) =>
  Array.from({ length: n }, (_, i) => new Date(Date.parse(`${start}T00:00:00Z`) + i * 86_400_000).toISOString().slice(0, 10));

const DEEL1_GAMES_K = [245, 262, 231, 208, 196, 182, 174, 248, 218, 190, 176, 168, 158, 147, 201, 178, 160, 148, 140, 132, 124];
const DEEL1_VISITORS_K = [68, 58, 46, 40, 36, 32, 30, 48, 40, 34, 30, 28, 25, 22, 41, 34, 29, 26, 24, 22, 20];

/** The design's Deel 1, as the pipeline embeds it: finished, all three weeks run. */
export const PF_PREVIEW_DEEL1: PrijsfavorietenPart = {
  ...PF_PREVIEW,
  campaign: {
    ...PF_PREVIEW.campaign,
    status: 'Afgerond',
    currentDay: 21,
    currentWeek: 3,
    pace: '~133.425 games / dag',
    periodLabel: 'Deel 1',
    live: false,
  },
  weeks: PF_PREVIEW.weeks.map((w) => ({ label: w.label, dates: w.dates, planned: false })),
  weekly: {
    ...PF_PREVIEW.weekly,
    rows: [
      { label: 'Unieke bezoekers', values: [298_140, 237_552, 196_884] },
      { label: 'Unieke spelers', values: [212_455, 161_633, 132_910] },
      { label: 'Aantal bezoeken', values: [512_480, 425_900, 315_917] },
      { label: 'Tijd in campagne', values: ['62.140u', '51.320u', '38.860u'] },
      { label: 'Games gespeeld', values: [1_106_318, 995_601, 700_000] },
    ],
  },
  series: { games: DEEL1_GAMES_K.map((k) => k * 1000), visitors: DEEL1_VISITORS_K.map((k) => k * 1000) },
  totals: { ...PF_PREVIEW.totals, avgVisitorsPerDay: 25_509 },
};

const DEEL2_DAYS = dayRange('2026-09-28', 21);
const upToDay4 = (k: number[]) => DEEL2_DAYS.map((_d, i) => (k[i] ?? 0) * 1000);

export const PF_PREVIEW_DEEL2: PrijsfavorietenPayload = {
  campaign: {
    name: 'AH Prijsfavorieten',
    start: '28 sep 2026',
    end: '18 okt 2026',
    status: 'In voortgang',
    currentDay: 4,
    totalDays: 21,
    currentWeek: 1,
    pace: '~115.578 games / dag',
    weeksLabel: '3 × 1 week',
    periodLabel: 'Deel 2',
    calendarWeeks: 'CW 40 / 41 / 42',
    live: true,
  },
  weeks: [
    { label: 'wk 1', dates: '28 sep – 4 okt' },
    { label: 'wk 2', dates: '5 – 11 okt', planned: true },
    { label: 'wk 3', dates: '12 – 18 okt', planned: true },
  ],
  weekly: {
    label: 'Overzicht per week',
    columns: ['WK 1', 'WK 2', 'WK 3'],
    rows: [
      { label: 'Unieke bezoekers', values: [121_480, null, null] },
      { label: 'Unieke spelers', values: [88_240, null, null] },
      { label: 'Aantal bezoeken', values: [214_905, null, null] },
      { label: 'Tijd in campagne', values: ['26.140u', null, null] },
      { label: 'Games gespeeld', values: [462_310, null, null] },
    ],
  },
  days: DEEL2_DAYS,
  series: { games: upToDay4([138, 126, 110, 104]), visitors: upToDay4([41, 32, 26, 22]) },
  totals: {
    uniqueVisitors: 121_480,
    avgVisitorsPerDay: 30_370,
    uniquePlayers: 88_240,
    visits: 214_905,
    timeInCampaign: '26.140u',
    avgTimePerUser: '17m 46s',
    avgSessionDuration: '7m 34s',
    gamesPlayed: 462_310,
    avgGamesPerPlayer: 5.24,
  },
  gameplay: {
    distribution: [
      { label: '1 game', pct: 34.1 },
      { label: '2 games', pct: 15.6 },
      { label: '3 games', pct: 11.2 },
      { label: '4 games', pct: 8.4 },
      { label: '5+ games', pct: 30.7 },
    ],
    // One week in: everybody has played in exactly one week.
    repeat: { allWeeksPct: 0, rows: [{ label: 'Eén week', pct: 100 }] },
  },
  behaviour: {
    infoPagesTotal: 11_940,
    topPages: [
      { name: 'Voorwaarden', value: 7_912 },
      { name: 'Hoe werkt het', value: 3_104 },
      { name: 'Veelgestelde vragen', value: 924 },
    ],
  },
  previous: PF_PREVIEW_DEEL1,
};
